import type { ReactNode } from 'react';
import { StatusDot } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import { FlowGlyph, FlowSpark } from './FlowGlyph';
import type { FlowNote, FlowStatus } from './flow-state';
import { chipWords, placeName, unitWord } from './flow-words';
import type { FlowCell, FlowPart } from './flow-types';
import { partName } from '../pools/pools-words';

/** The few words that say why a place is not simply flowing; at most two, the most urgent first. */
export function FlowChips({ notes, max = 2 }: { notes: FlowNote[]; max?: number }): ReactNode {
  const f = useFormat();
  if (notes.length === 0) return null;
  const shown = notes.slice(0, max);
  return (
    <span className="fl-chips">
      {shown.map((n) => (
        <span key={`${n.kind}-${n.reason ?? ''}`} className={`fl-chip fl-chip--${n.tone}`}>
          {chipWords(f, n)}
        </span>
      ))}
      {notes.length > shown.length ? (
        <span className="fl-chip fl-chip--more">+{notes.length - shown.length}</span>
      ) : null}
    </span>
  );
}

/** "8 being worked" with a pulse while it is true, and the pages a minute leaving. */
export function FlowNow({ cell, f }: { cell: FlowCell; f: Formatter }): ReactNode {
  return (
    <span className="fl-stage__now">
      <span className={cell.working > 0 ? 'fl-work fl-work--on' : 'fl-work'}>
        {cell.working > 0 ? <StatusDot tone="accent" live size="sm" label="Working now" /> : null}
        {f.int(cell.working)} working
      </span>
      <span className={cell.pagesPerMinute > 0 ? 'fl-rate fl-rate--on' : 'fl-rate'}>
        {f.int(cell.pagesPerMinute)} <span className="fl-rate__unit">pages/min</span>
      </span>
    </span>
  );
}

/** Everything at the stage now, large; the number steps when it changes. */
function StageCount({ cell, here }: { cell: FlowCell; here: number }): ReactNode {
  const f = useFormat();
  const held = cell.holds.reduce((n, h) => n + h.count, 0);
  return (
    <span className="fl-stage__count">
      <strong key={here} className="fl-num">
        {f.int(here)}
      </strong>
      <span className="fl-stage__unit">
        {unitWord(cell.unit, here)} {here > 0 && held === here ? 'held' : 'here'}
      </span>
    </span>
  );
}

/**
 * Where work stopped, drawn where it stopped: a mark that has halted on the stage's edge and pulses, red when a
 * person at Ademero must act, amber while Paige tries again. With reduced motion it is a still, outlined mark.
 */
export function FlowHalt({ tone }: { tone: FlowStatus['tone'] }): ReactNode {
  if (tone !== 'stuck' && tone !== 'slow') return null;
  return (
    <span className={`fl-halt fl-halt--${tone}`} aria-hidden="true">
      <i className="fl-halt__mark" />
    </span>
  );
}

/** "Waiting on Page reader: 0 of 2 ready" on a stage a pool or service is not feeding. */
function Starved({ part }: { part: FlowPart }): ReactNode {
  return (
    <span className={`fl-chip fl-chip--${part.state === 'down' ? 'stuck' : 'slow'} fl-starved`}>
      Waiting on {partName(part.id)}: {part.ready} of {part.wanted} ready
    </span>
  );
}

export interface FlowStageProps {
  cell: FlowCell;
  status: FlowStatus;
  selected: boolean;
  onSelect: () => void;
  className?: string;
  /** A pool or service down or short that feeds this stage. */
  starved?: FlowPart | null | undefined;
  /** The cards strip points here (a part that feeds it is hovered there). */
  lit?: boolean | undefined;
  /** The preview of its oldest items while hovered; null when not. */
  peek?: ReactNode;
  onPeek?: ((on: boolean) => void) | undefined;
}

function classesOf(p: FlowStageProps): string {
  return [
    'fl-stage',
    `fl-stage--${p.status.tone}`,
    p.selected && 'fl-stage--open',
    p.starved && 'fl-stage--starved',
    p.lit && 'fl-stage--lit',
    p.className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * One stage of the pipeline: how many sit there, how many are being worked, the pages a minute leaving, a
 * quarter hour of that as a line, and, when it is not simply flowing, why. A button: it opens the detail.
 */
export function FlowStage(props: FlowStageProps): ReactNode {
  const { cell, status, selected, onSelect, starved, peek, onPeek } = props;
  const f = useFormat();
  return (
    <button
      type="button"
      className={classesOf(props)}
      aria-pressed={selected}
      data-place={cell.place}
      data-tone={status.tone}
      onClick={onSelect}
      onMouseEnter={() => onPeek?.(true)}
      onMouseLeave={() => onPeek?.(false)}
      onFocus={() => onPeek?.(true)}
      onBlur={() => onPeek?.(false)}
    >
      <FlowHalt tone={status.tone} />
      <span className="fl-stage__head">
        <FlowGlyph tone={status.tone} />
        <span className="fl-stage__name">{placeName[cell.place]}</span>
      </span>
      <StageCount cell={cell} here={status.here} />
      <FlowNow cell={cell} f={f} />
      <FlowSpark
        values={cell.series}
        label={`${placeName[cell.place]}: ${f.int(cell.pages15m)} pages in the last 15 minutes`}
      />
      {starved ? <Starved part={starved} /> : null}
      <FlowChips notes={status.notes} />
      {peek}
    </button>
  );
}
